import {
  initialProjectState,
  type ProjectAction,
  type ProjectEffect,
  type ProjectEnv,
  type ProjectState,
  reduceProject,
  uid,
} from '@better-lights/core';
import { create } from 'zustand';
import { type ProjectFile, toProjectRecord } from '../storage/record.ts';
import { useSettings } from './settingsStore.ts';

/**
 * Tier 1 — the open project (undoable document). State and undo/redo semantics are the
 * core-ts project reducer (fixed behaviors are core's defaults): history is the
 * `runs` array only, max 100 steps. Tool / selection live here too because the reducer's
 * rules depend on them (e.g. switching tool finishes the run being drawn).
 */
export interface DocumentStore extends ProjectState {
  /** The saved file this state was loaded from (metadata + unknown keys), null when closed. */
  file: ProjectFile | null;
  dispatch: (action: ProjectAction) => void;
  open: (file: ProjectFile) => void;
  close: () => void;
  /** Replaces the saved-file metadata (after rename / autosave) without touching the document. */
  setFile: (file: ProjectFile) => void;
}

/** File URIs the reducer asked to delete; collected after the next save. */
export const pendingDeletes: string[] = [];

function env(): ProjectEnv {
  const s = useSettings.getState();
  return { uid, settings: { legendLabels: s.legendLabels, rememberLegend: s.rememberLegend } };
}

function applyEffects(effects: ProjectEffect[]): void {
  for (const e of effects) {
    if (e.type === 'rememberLegendLabel') useSettings.getState().setLegendLabel(e.hex, e.label);
    else if (e.type === 'deleteFile') pendingDeletes.push(e.uri);
  }
}

export const useDocument = create<DocumentStore>((set, get) => ({
  ...initialProjectState(),
  file: null,
  dispatch: (action) => {
    const { file: _f, dispatch: _d, open: _o, close: _c, setFile: _s, ...state } = get();
    const { state: next, effects } = reduceProject(state, action, env());
    if (next !== state) set(next);
    applyEffects(effects);
  },
  open: (file) => {
    const { state } = reduceProject(
      initialProjectState(),
      { type: 'loadProject', record: toProjectRecord(file) },
      env(),
    );
    set({ ...state, file });
  },
  close: () => set({ ...initialProjectState(), file: null }),
  setFile: (file) => set({ file }),
}));

export const dispatch = (action: ProjectAction) => useDocument.getState().dispatch(action);

/** The reducer state without store functions / file (for pure helpers). */
export function projectState(): ProjectState {
  const { file: _f, dispatch: _d, open: _o, close: _c, setFile: _s, ...state } = useDocument.getState();
  return state;
}
